import { useState } from 'react';
import { Code, Type } from 'lucide-react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import WysiwygEditor from '../WysiwygEditor';
import CodeEditorModal from '../CodeEditorModal';

/**
 * HtmlContentEditor
 *
 * Shared component that wraps the simple/advanced editor selection flow.
 * Used anywhere HTML content is edited in the admin panel.
 *
 * Props:
 * - value (string) — current HTML content
 * - onChange (function) — callback when content changes
 * - label (string, optional) — field label shown above the editor
 * - context (string, optional) — passed to WysiwygEditor for image upload context
 * - placeholder (string, optional)
 * - defaultMode ('wysiwyg' | 'code' | null) — initial mode; null shows chooser
 * - showModeChooser (boolean, optional) — if false, skip chooser and go directly to defaultMode
 * - onImageUpload (function, optional) — image upload handler for WysiwygEditor
 * - pageId (string, optional) — used by CodeEditorModal for draft auto-save key
 */
const HtmlContentEditor = ({
  value,
  onChange,
  label,
  context,
  placeholder,
  defaultMode = null,
  showModeChooser = true,
  onImageUpload,
  pageId,
}) => {
  const initialMode = showModeChooser ? defaultMode : (defaultMode || 'wysiwyg');
  const [editorMode, setEditorMode] = useState(initialMode);
  const [codeEditorOpen, setCodeEditorOpen] = useState(false);

  return (
    <div>
      {label && (
        <div className="flex items-center justify-between mb-2">
          <Label>{label}</Label>
          {editorMode !== null && showModeChooser && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditorMode(null)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Switch Editor Mode
            </Button>
          )}
        </div>
      )}

      {!label && editorMode !== null && showModeChooser && (
        <div className="flex justify-end mb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setEditorMode(null)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Switch Editor Mode
          </Button>
        </div>
      )}

      {/* Mode Chooser */}
      {editorMode === null && (
        <div className="py-8">
          <h3 className="text-lg font-semibold text-center mb-2">Choose Your Editor</h3>
          <p className="text-sm text-gray-600 text-center mb-8">
            Select how you want to edit your page content
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Simple Editor Option */}
            <button
              type="button"
              onClick={() => setEditorMode('wysiwyg')}
              className="group p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
            >
              <div className="flex items-center justify-center mb-4">
                <div className="p-4 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                  <Type className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <h4 className="text-lg font-semibold text-center mb-2">Simple Editor</h4>
              <p className="text-sm text-gray-600 text-center mb-4">
                Visual editor with formatting toolbar
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Easy to use, like a word processor</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Best for basic text and images</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>No coding knowledge required</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✗</span>
                  <span>Limited layout options</span>
                </li>
              </ul>
            </button>

            {/* Advanced Editor Option */}
            <button
              type="button"
              onClick={() => setEditorMode('code')}
              className="group p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
            >
              <div className="flex items-center justify-center mb-4">
                <div className="p-4 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors">
                  <Code className="h-8 w-8 text-purple-600" />
                </div>
              </div>
              <h4 className="text-lg font-semibold text-center mb-2">Advanced Editor</h4>
              <p className="text-sm text-gray-600 text-center mb-4">
                Full HTML/CSS code editor
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Complete layout control</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Custom CSS Grid &amp; Flexbox layouts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Complex multi-column designs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-600 mr-2">⚠</span>
                  <span>Requires HTML/CSS knowledge</span>
                </li>
              </ul>
            </button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 max-w-2xl mx-auto">
              <strong>Tip:</strong> The simple editor will strip complex HTML if you switch back from the advanced editor.
              Choose the advanced editor if you need custom layouts or styling.
            </p>
          </div>
        </div>
      )}

      {/* WYSIWYG Editor */}
      {editorMode === 'wysiwyg' && (
        <div className="bg-background border rounded">
          <WysiwygEditor
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            context={context}
            onImageUpload={onImageUpload}
            onOpenCodeEditor={() => {
              setEditorMode('code');
              setCodeEditorOpen(true);
            }}
          />
        </div>
      )}

      {/* Code Editor (button + modal) */}
      {editorMode === 'code' && (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCodeEditorOpen(true)}
            className="w-full border-2 border-dashed h-24 hover:bg-purple-50 hover:border-purple-300"
          >
            <Code className="h-5 w-5 mr-2 text-purple-600" />
            Open Code Editor
          </Button>
          {value && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded">
              Content preview: {value.substring(0, 100)}...
            </div>
          )}
        </>
      )}

      {/* Code Editor Modal */}
      {(editorMode === 'code' || editorMode === 'wysiwyg') && (
        <CodeEditorModal
          open={codeEditorOpen}
          onOpenChange={setCodeEditorOpen}
          value={value}
          onChange={onChange}
          pageId={pageId || 'new'}
        />
      )}
    </div>
  );
};

export { HtmlContentEditor };
